/* eslint-disable import/export */
import { array, eq, monoid, number, ord, pipeable, record, semigroup, set } from 'fp-ts';
import { flow, pipe, tuple } from 'fp-ts/function';
import { constant } from './function.js';
import * as nonEmptyArray from './nonEmptyArray.js';
import * as option from './option.js';
export * from 'fp-ts/Array';
export const neighbors = (middle) => (as) => {
    const i = as.indexOf(middle);
    return i === -1
        ? [option.none, option.none]
        : [option.fromNullable(as[i - 1]), option.fromNullable(as[i + 1])];
};
/**
 * Remove duplicates from an array, keeping the first occurrence of an element. Sorts the returned
 * unique elements in order.
 */
export const uniqSort = (o) => flow(array.uniq(o), array.sort(o));
/**
 * Check whether an array contains duplicates.
 */
export const isUnique = (e) => (as) => number.Eq.equals(as.length, pipe(as, array.uniq(e)).length);
/**
 * Compare the elements of two arrays, disregarding order and duplicates.
 */
export const getSetEq = (e) => pipe(set.getEq(e), eq.contramap(set.fromArray(e)));
/**
 * "Reverses" the index of an Array, from `{ [number]: A }` to `{ [string]: number }`, number here
 * being the index in the given Array.
 *
 * To build the index, a function f `(a: A) => string` must be given. To emulate the behaviour of
 * array.findIndex, elements that produce a key that is already present in the reverse index are
 * ignored. If collisions are possible or duplicate (under f) are present, f should also be used to
 * calculate the key to look up the index.
 *
 * @example
 * assert.deepEqual(
 *   {
 *     a: 0,
 *     b: 1,
 *     c: 3,
 *   },
 *   pipe(
 *     [{_id: 'a'}, {_id: 'b'}, {_id: 'a'}, {_id: 'c'}],
 *     reverseIndexFirst((x) => x._id),
 *   )
 * )
 */
export const reverseIndexFirst = (f) => {
    const fromArrayMap = record.fromFoldableMap(semigroup.first(), array.Foldable);
    return flow(array.mapWithIndex(tuple), (as) => fromArrayMap(as, ([i, a]) => [f(a), i]));
};
export function flatTraverse(F) {
    return (f) => flow(array.traverse(F)(f), pipeable.map(F)(array.flatten));
}
export const lookupOrThrow = (i) => flow(array.lookup(i), option.getOrThrow(() => new Error(`Array index ${i} out of bounds`)));
/**
 * Tells if the provided predicate holds true at least for one element.
 */
export const some = (p) => array.some(p);
export const sum = monoid.concatAll(number.MonoidSum);
/**
 * Similar to array.partition, but can shrink both the left and right hand type
 * if the input type is not infinite.
 */
export const partitionUnion = array.partition;
export const max = (o) => flow(nonEmptyArray.fromArray, option.map(nonEmptyArray.max(o)));
export const min = (o) => flow(nonEmptyArray.fromArray, option.map(nonEmptyArray.min(o)));
export const shuffle = array.matchW(constant([]), nonEmptyArray.shuffle);
/**
 * Returns true iff the array contains exactly one element.
 * Asserts that the array is non-empty.
 */
export const isSingleton = (a) => a.length === 1;
export const matchSingleton = ({ onEmpty, onSingleton, otherwise, }) => array.match(onEmpty, nonEmptyArray.matchSingleton({ onSingleton, otherwise }));
export const singletonElement = (a) => isSingleton(a) ? option.some(a[0]) : option.none;
export const appendIf = (condition, a) => (as) => condition ? pipe(as, array.append(a())) : as;
/**
 * Takes an array of closed intervals (tuples) and merges each group of contiguous or overlapping
 * intervals into a single interval.
 */
export const joinOverlappingIntervals = (o) => flow(array.sort(ord.tuple(o, o)), array.reduce(array.zero(), (acc, [start, end]) => pipe(acc, array.matchRight(() => [[start, end]], (init, [lastStart, lastEnd]) => ord.leq(o)(start, lastEnd)
    ? [...init, [lastStart, ord.max(o)(lastEnd, end)]]
    : [...acc, [start, end]]))));
export const size = (a) => array.size(a);
export const elemV = (e) => (as) => (a) => array.elem(e)(a, as);
/**
 * Count the number of elements matching a predicate.
 */
export const count = (p) => flow(array.filter(p), size);
export const from = (...as) => as;
//# sourceMappingURL=array.js.map